/**
 * Componente AlertSkeleton
 * 
 * Skeleton loading para componentes de alerta.
 * Exibe um placeholder animado enquanto os dados estão carregando.
 * 
 * @module components/Dashboard/AlertsSection/AlertSkeleton
 */

import React from 'react';
import PropTypes from 'prop-types';
import Card from '../../UI/Card';

/**
 * Componente de Skeleton para Alertas
 * 
 * Exibe um placeholder animado com efeito de "shimmer" (brilho)
 * para indicar que os dados estão carregando.
 * 
 * @param {object} props - Propriedades do componente
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes (data-testid)
 * 
 * @returns {JSX.Element} Componente de skeleton
 * 
 * @example
 * ```jsx
 * {isLoading ? (
 *     <AlertSkeleton />
 * ) : (
 *     <PontoDesviosAlert {...props} />
 * )}
 * ```
 */
const AlertSkeleton = ({ className = '', testId = null }) => {
    return (
        <div className={className} data-testid={testId || 'alert-skeleton'}>
            {/* Título skeleton */}
            <div className="h-6 w-64 bg-gray-200 rounded mb-4 animate-pulse" />
            
            <Card className="p-4">
                {/* Conteúdo skeleton */}
                <div className="space-y-4">
                    {/* Linha 1 */}
                    <div className="flex items-center space-x-4">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </div>
                    
                    {/* Linha 2 */}
                    <div className="flex items-center space-x-4">
                        <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-26 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-22 bg-gray-200 rounded animate-pulse" />
                    </div>
                    
                    {/* Linha 3 */}
                    <div className="flex items-center space-x-4">
                        <div className="h-4 w-30 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </div>
                </div>
            </Card>
        </div>
    );
};

AlertSkeleton.propTypes = {
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes (data-testid) */
    testId: PropTypes.string,
};

AlertSkeleton.defaultProps = {
    className: '',
    testId: null,
};

export default AlertSkeleton;

